import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CoachChangeList from '../WorkoutDebrief/CoachChangeList';
import { buildPreviewFromChanges } from '../../utils/plan_changes';

export default function ChatProposalCard({ messageId, proposal, proposalAction, onApply, onIgnore, plan = null }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!proposal) return null;

  const title = proposal?.title || t('chat.proposal.title_fallback');
  const rationale = proposal?.rationale || '';
  const kind = String(proposal?.kind || '');
  const changes = kind === 'plan_changes' && Array.isArray(proposal?.changes) ? proposal.changes : [];
  const preview = changes.length > 0 ? buildPreviewFromChanges(changes, plan) : [];

  const canApply = Boolean(
    kind === 'workout_patches' ||
    kind === 'plan_regeneration' ||
    (kind === 'plan_changes' && changes.length > 0)
  );
  const decided = proposalAction === 'ignored' || proposalAction === 'applied';

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn?.(proposal, { messageId });
    } catch (e) {
      setError(e?.message || t('chat.proposal.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 border border-indigo-200 bg-indigo-50 rounded-xl p-3">
      <div className="text-[11px] font-extrabold uppercase tracking-wide text-indigo-700">
        {t('chat.proposal.label')}
      </div>
      <div className="mt-1 text-sm font-extrabold text-gray-900">
        {title}
      </div>
      {rationale ? (
        <div className="mt-1 text-sm text-gray-700">
          {rationale}
        </div>
      ) : null}

      {preview.length > 0 ? (
        <CoachChangeList preview={preview} className="mt-3" />
      ) : null}

      <div className="mt-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            disabled={!canApply || busy || decided}
            onClick={() => {
              if (!canApply || busy || decided) return;
              run(onApply);
            }}
            className={[
              'w-full sm:w-auto px-3 py-2 rounded-lg font-extrabold text-sm',
              !canApply || busy || decided ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'
            ].join(' ')}
          >
            {proposalAction === 'applied'
              ? t('chat.proposal.applied')
              : (busy ? t('chat.proposal.applying') : t('chat.proposal.apply'))}
          </button>

          <button
            type="button"
            disabled={busy || decided}
            onClick={() => {
              if (busy || decided) return;
              run(onIgnore);
            }}
            className={[
              'w-full sm:w-auto px-3 py-2 rounded-lg font-extrabold text-sm',
              busy || decided ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-100'
            ].join(' ')}
          >
            {proposalAction === 'ignored' ? t('chat.proposal.ignored') : t('chat.proposal.ignore')}
          </button>
        </div>

        {error ? (
          <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-xs font-bold text-red-700">{error}</div>
        ) : null}

        <div className="mt-2 text-[11px] text-gray-600">
          {t('chat.proposal.hint')}
        </div>
      </div>
    </div>
  );
}
